import {
  Elements,
  PaymentElement,
  useElements,
  useStripe,
} from "@stripe/react-stripe-js"
import { type Stripe, loadStripe } from "@stripe/stripe-js"
import { type FormEvent, useMemo, useState } from "react"

import { formatShortDate } from "@/components/billing/agent-purchase-summary"
import { Button } from "@/components/ui/button"
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import {
  landingPageCheckoutIntentPath,
  landingPageFulfillPath,
  landingPageStartTrialPath,
  landingPageTrialSetupIntentPath,
  legalPrivacyPath,
  legalTermsPath,
} from "@/routes"

// IMP-394: the free-trial shape on the pricing prop when a trial is offered. A
// trial zeroes today's charge — the modal leads with it and captures a card via
// a SetupIntent instead of charging.
export interface LandingTrialInfo {
  days: number
  trial_end_date: string
}

interface ActivateAgentModalProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  // landing_page_uid — the slug in /lp/:uid; addresses the default checkout
  // endpoints (also used as the stable key in copy).
  landingUid: string
  // IMP-287: override the checkout endpoints (e.g. the partner landing page
  // posts to /p/:uid/... instead of /lp/:uid/...). Default to the account paths.
  checkoutIntentPath?: string
  fulfillPath?: string
  // IMP-394: override the trial endpoints (partner landing posts to /p/:uid/...).
  trialSetupIntentPath?: string
  startTrialPath?: string
  agentName: string
  // IMP-498: which job is being bought. Rides the checkout/trial requests so a
  // hub page can sell any of the launch trio through the same endpoints.
  // Omitted → the server's default (receptionist), which is what /lp/:uid wants.
  archetypeSlug?: string
  // IMP-498: the product line in the order summary. Defaults to the
  // receptionist, the only thing this modal used to sell.
  productLabel?: string
  retailPriceCents: number
  currency: string
  // IMP-270: when the product bundles a monthly minutes allowance, show it plus
  // the marked-up per-minute overage rate (dollars). Both absent → no overage line.
  includedMinutes?: number
  overageRetailPerMinute?: number
  // IMP-394: present → this is a free trial ($0 today, billing at trial-end).
  trial?: LandingTrialInfo | null
  // Platform-level publishable key (pk_live_… / pk_test_…). The
  // payment is scoped to the partner via stripeAccount on the loader.
  publishableKey: string
  // The partner's connected Stripe account id (acct_…). Stripe.js
  // mounts the Elements against this account so the PaymentIntent we
  // create on the server lives on the partner's books, not Imperative's.
  connectedAccountId: string
  // IMP-504: seed the buyer-email field (e.g. the email the assessment
  // captured moments earlier) — editable, never locked.
  defaultEmail?: string
}

// In-memory cache: Stripe.js requires one loader instance per
// {publishableKey, stripeAccount} pair. Without caching we'd download
// the bundle every time the modal opens, which both flickers the form
// and burns Stripe rate-limit headroom.
const stripeCache = new Map<string, Promise<Stripe | null>>()

function getStripe(publishableKey: string, stripeAccount: string) {
  const key = `${publishableKey}::${stripeAccount}`
  let promise = stripeCache.get(key)
  if (!promise) {
    promise = loadStripe(publishableKey, { stripeAccount })
    stripeCache.set(key, promise)
  }
  return promise
}

function csrfToken() {
  return (
    document
      .querySelector('meta[name="csrf-token"]')
      ?.getAttribute("content") ?? ""
  )
}

function browserTimezone(): string {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone || "UTC"
  } catch {
    return "UTC"
  }
}

export function ActivateAgentModal(props: ActivateAgentModalProps) {
  const { open, onOpenChange, publishableKey, connectedAccountId, trial } =
    props

  const stripePromise = useMemo(
    () => getStripe(publishableKey, connectedAccountId),
    [publishableKey, connectedAccountId],
  )

  const isTrial = trial != null

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <Elements
          stripe={stripePromise}
          options={
            isTrial
              ? {
                  // IMP-394: a trial charges nothing today — capture a card via a
                  // SetupIntent (deferred, confirmed server-side on submit).
                  mode: "setup",
                  currency: props.currency,
                  paymentMethodTypes: ["card", "us_bank_account"],
                  appearance: {
                    theme: "stripe",
                    variables: { fontSizeBase: "0.875rem" },
                  },
                }
              : {
                  // Deferred mode: render the PaymentElement before a
                  // PaymentIntent exists. We create the intent server-side on
                  // submit (with the buyer email), then confirm with its
                  // client_secret — Stripe's deferred-intent flow.
                  //
                  // IMP-283: pin the methods to card + US bank account here AND on
                  // the PaymentIntent (payment_method_types). The two configs must
                  // match, or confirmation fails.
                  mode: "payment",
                  amount: props.retailPriceCents,
                  currency: props.currency,
                  setupFutureUsage: "off_session",
                  paymentMethodTypes: ["card", "us_bank_account"],
                  appearance: {
                    theme: "stripe",
                    variables: { fontSizeBase: "0.875rem" },
                  },
                }
          }
        >
          <ActivateForm {...props} />
        </Elements>
      </DialogContent>
    </Dialog>
  )
}

function ActivateForm({
  onOpenChange,
  landingUid,
  checkoutIntentPath,
  fulfillPath,
  trialSetupIntentPath,
  startTrialPath,
  agentName,
  archetypeSlug,
  productLabel = "AI Receptionist",
  retailPriceCents,
  currency,
  includedMinutes,
  overageRetailPerMinute,
  trial,
  defaultEmail,
}: ActivateAgentModalProps) {
  const stripe = useStripe()
  const elements = useElements()
  const [email, setEmail] = useState(defaultEmail ?? "")
  const [submitting, setSubmitting] = useState(false)
  const [succeeded, setSucceeded] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const isTrial = trial != null

  const priceLabel = useMemo(
    () =>
      new Intl.NumberFormat("en-US", {
        style: "currency",
        currency: currency.toUpperCase(),
        minimumFractionDigits: 0,
      }).format(retailPriceCents / 100),
    [retailPriceCents, currency],
  )

  // IMP-270: "Includes 500 minutes/month, then $0.30/minute". The per-minute
  // rate is precise to 4dp, so allow up to 4 fraction digits (min 2).
  const overageLabel = useMemo(() => {
    if (!includedMinutes || overageRetailPerMinute == null) return null
    const rate = new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: currency.toUpperCase(),
      minimumFractionDigits: 2,
      maximumFractionDigits: 4,
    }).format(overageRetailPerMinute)
    return `Includes ${includedMinutes.toLocaleString()} minutes/month, then ${rate}/minute`
  }, [includedMinutes, overageRetailPerMinute, currency])

  const actionLabel = isTrial ? "Start free trial" : `Pay ${priceLabel}`

  // Charge-now purchase: create a PaymentIntent, confirm the card, fulfill.
  async function submitPurchase(): Promise<void> {
    if (!stripe || !elements) return

    const submitResult = await elements.submit()
    if (submitResult.error) {
      throw new Error(
        submitResult.error.message ?? "Please check your card details.",
      )
    }

    const intent = (await postJson(
      checkoutIntentPath ?? landingPageCheckoutIntentPath(landingUid),
      {
        email: email.trim(),
        timezone: browserTimezone(),
        archetype_slug: archetypeSlug,
      },
    )) as { client_secret?: string; error?: string }
    if (!intent.client_secret) {
      throw new Error(intent.error ?? "Couldn't start the payment.")
    }

    const { error: confirmError, paymentIntent } = await stripe.confirmPayment({
      elements,
      clientSecret: intent.client_secret,
      confirmParams: { return_url: window.location.href },
      redirect: "if_required",
    })
    if (confirmError) {
      throw new Error(
        confirmError.message ?? "Your payment could not be completed.",
      )
    }

    // Fulfill (provision + email). The webhook is the backstop, so a soft
    // failure here still resolves — we don't block the buyer.
    if (paymentIntent?.id) {
      try {
        await postJson(fulfillPath ?? landingPageFulfillPath(landingUid), {
          payment_intent_id: paymentIntent.id,
        })
      } catch {
        // Non-fatal: the payment_intent.succeeded webhook will fulfill.
      }
    }
  }

  // IMP-394 free trial: capture a card via a SetupIntent ($0 today), then start
  // the trial. No webhook backstop — a SetupIntent moves no money, so a dropped
  // start just leaves the card captured and the trial still offered on retry.
  async function submitTrial(): Promise<void> {
    if (!stripe || !elements) return

    const submitResult = await elements.submit()
    if (submitResult.error) {
      throw new Error(
        submitResult.error.message ?? "Please check your card details.",
      )
    }

    const timezone = browserTimezone()
    const intent = (await postJson(
      trialSetupIntentPath ?? landingPageTrialSetupIntentPath(landingUid),
      { email: email.trim(), timezone, archetype_slug: archetypeSlug },
    )) as { client_secret?: string; setup_intent_id?: string; error?: string }
    if (!intent.client_secret) {
      throw new Error(intent.error ?? "Couldn't start the free trial.")
    }

    const { error: confirmError, setupIntent } = await stripe.confirmSetup({
      elements,
      clientSecret: intent.client_secret,
      confirmParams: { return_url: window.location.href },
      redirect: "if_required",
    })
    if (confirmError) {
      throw new Error(
        confirmError.message ?? "We couldn't save your card. Try again.",
      )
    }

    const result = (await postJson(
      startTrialPath ?? landingPageStartTrialPath(landingUid),
      {
        setup_intent_id: setupIntent?.id ?? intent.setup_intent_id,
        email: email.trim(),
        timezone,
      },
    )) as { status?: string; error?: string }
    if (result.status !== "ok") {
      throw new Error(result.error ?? "Couldn't start the free trial.")
    }
  }

  async function handleSubmit(e: FormEvent) {
    e.preventDefault()
    if (!stripe || !elements) return

    setError(null)
    setSubmitting(true)
    try {
      if (!email.trim()) {
        setError("Enter your email so we can send you the activation link.")
        return
      }
      if (isTrial) {
        await submitTrial()
      } else {
        await submitPurchase()
      }
      setSucceeded(true)
    } catch (err) {
      setError(err instanceof Error ? err.message : "Something went wrong.")
    } finally {
      setSubmitting(false)
    }
  }

  if (succeeded) {
    return (
      <>
        <DialogHeader>
          <DialogTitle>Check your email</DialogTitle>
          <DialogDescription>
            {isTrial ? (
              <>
                Your {trial.days}-day free trial is on. We sent a link to{" "}
                <strong>{email}</strong> to finish setting up your {agentName}.
              </>
            ) : (
              <>
                Payment received. We sent a link to <strong>{email}</strong> to
                finish setting up your {agentName}.
              </>
            )}
          </DialogDescription>
        </DialogHeader>
        <DialogFooter className="mt-6">
          <Button type="button" onClick={() => onOpenChange(false)}>
            Done
          </Button>
        </DialogFooter>
      </>
    )
  }

  return (
    <form
      onSubmit={(e) => {
        void handleSubmit(e)
      }}
    >
      <DialogHeader>
        <DialogTitle>
          {isTrial ? "Start free trial" : "Confirm order"}
        </DialogTitle>
      </DialogHeader>

      <div className="mt-4 space-y-4">
        <div className="bg-muted/40 -mx-6 space-y-4 border-y px-6 py-5">
          <div>
            <p className="text-sm font-medium">{productLabel}</p>
            <p className="mt-1 flex items-baseline gap-2">
              {isTrial ? (
                <>
                  <span className="text-4xl font-medium">
                    {trial.days} days free
                  </span>
                  <span className="text-muted-foreground text-sm">
                    then {priceLabel}/month, starting{" "}
                    {formatShortDate(trial.trial_end_date)}
                  </span>
                </>
              ) : (
                <>
                  <span className="text-4xl font-medium">{priceLabel}</span>
                  <span className="text-muted-foreground text-sm">
                    per month, starting today
                  </span>
                </>
              )}
            </p>
            {overageLabel && (
              <p className="text-muted-foreground mt-2 text-sm">
                {overageLabel}
              </p>
            )}
          </div>
        </div>

        <div className="space-y-1.5">
          <Label htmlFor="activate-email">Email</Label>
          <Input
            id="activate-email"
            type="email"
            required
            autoComplete="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
          />
          <p className="text-muted-foreground text-xs">
            Activation link and receipt will be sent here.
          </p>
        </div>

        <PaymentElement
          options={{ layout: "tabs", wallets: { link: "never" } }}
        />

        {error && <p className="text-destructive text-sm">{error}</p>}
      </div>

      <DialogFooter className="mt-6 flex-col gap-3 sm:flex-col sm:space-x-0">
        <Button
          type="submit"
          className="w-full"
          disabled={!stripe || submitting}
          loading={submitting}
        >
          {submitting ? "Processing…" : actionLabel}
        </Button>
        {/* IMP-265/277: agreement to the legal terms, shown below the button. */}
        <p className="text-muted-foreground text-center text-xs text-balance">
          By {isTrial ? "starting a trial" : "purchasing"}, you agree to the{" "}
          <a
            href={legalTermsPath()}
            target="_blank"
            rel="noopener noreferrer"
            className="underline underline-offset-2"
          >
            Terms of Service
          </a>{" "}
          and{" "}
          <a
            href={legalPrivacyPath()}
            target="_blank"
            rel="noopener noreferrer"
            className="underline underline-offset-2"
          >
            Privacy Policy
          </a>
          .
        </p>
      </DialogFooter>
    </form>
  )
}

async function postJson(
  url: string,
  body: Record<string, unknown>,
): Promise<Record<string, unknown>> {
  const res = await fetch(url, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      Accept: "application/json",
      "X-CSRF-Token": csrfToken(),
    },
    credentials: "same-origin",
    body: JSON.stringify(body),
  })
  return (await res.json().catch(() => ({}))) as Record<string, unknown>
}
